import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "./vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const vanAvecChauffeur: ChauffeurPage = {
  slug: "van-avec-chauffeur",
  title: "Van avec chauffeur — VTC 7 places",
  subtitle:
    "Mercedes Classe V avec chauffeur : l'espace pour la famille, les petits groupes et les bagages sur la Côte d'Azur.",
  keywords: [
    "van avec chauffeur Cannes",
    "VTC 7 places",
    "monospace avec chauffeur Nice aéroport",
    "minivan chauffeur famille Côte d'Azur",
  ],
  hero: {
    title: "Van avec chauffeur sur la Côte d'Azur — VTC 7 places",
    subtitle:
      "Mercedes Classe V : jusqu'à 7 passagers et leurs bagages dans un même véhicule confortable. Idéal famille, golf, ski et transferts aéroport.",
    keywords: ["van avec chauffeur", "VTC 7 places", "Classe V"],
    imageSrc: heroImage.src,
    imageAlt: "Mercedes Classe V (van) avec chauffeur à Cannes",
    badges: ["Jusqu'à 7 passagers", "Grand volume bagages", "Chauffeur dédié"],
  },
  benefits: {
    heading: "Pourquoi le van avec chauffeur KS Rent Services",
    items: [
      {
        title: "7 places + bagages",
        description:
          "Jusqu'à 7 passagers et un grand volume de bagages : tout le monde et tout le matériel dans un seul véhicule.",
      },
      {
        title: "Confort van premium",
        description:
          "Espace généreux, sièges individuels et accès facile : le confort d'un monospace haut de gamme, pas d'une navette standard.",
      },
      {
        title: "Famille, golf & ski",
        description:
          "Sacs de golf, équipements de ski, poussettes et valises : le van absorbe les départs en groupe sans compromis.",
      },
      {
        title: "Un seul véhicule",
        description:
          "Pas de groupe éclaté sur deux berlines : vous voyagez ensemble, avec un seul chauffeur et un seul interlocuteur.",
      },
    ],
  },
  services: {
    heading: "Prestations du van avec chauffeur",
    services: [
      {
        title: "Transferts aéroport en famille",
        text: "Aéroport de Nice et gares : la famille, les amis et tous les bagages en un seul trajet.",
      },
      {
        title: "Golf & ski",
        text: "Départs golf sur la Côte d'Azur et liaisons vers les stations, avec la place pour tous les équipements.",
      },
      {
        title: "Sorties de groupe",
        text: "Excursions, dîners et événements à plusieurs : un van confortable et un chauffeur à votre disposition.",
      },
      {
        title: "Mise à disposition",
        text: "Van et chauffeur à l'heure ou à la journée pour vos programmes en groupe.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & aéroport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "L'espace et le confort, pour voyager groupés",
    subheading: "Le bon véhicule entre la berline et le minibus VIP.",
    paragraphs: [
      "La Mercedes Classe V réunit jusqu'à 7 personnes et leurs bagages dans un van confortable, idéal pour les familles, les amis et les petits groupes.",
      "Plus spacieux qu'une berline, plus maniable qu'un minibus : c'est le véhicule juste pour vos transferts aéroport et vos sorties à plusieurs.",
      "Un interlocuteur unique, des forfaits transparents et la flexibilité d'ajuster votre programme à la dernière minute.",
    ],
    bullets: [
      "Van jusqu'à 7 passagers",
      "Forfaits aéroport transparents",
      "Disponibilité 24h/24, 7j/7",
    ],
  },
  process: {
    heading: "Comment réserver votre van avec chauffeur",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Recevez un forfait clair ou une estimation au kilomètre.",
      "Affectation de la Classe V et de votre chauffeur dédié.",
      "Prise en charge groupée, ponctuelle et confortable.",
    ],
  },
  faq: [
    {
      q: "Combien de passagers le van accueille-t-il ?",
      a: "La Mercedes Classe V transporte jusqu'à 7 passagers avec un grand volume de bagages, idéale pour les familles et les petits groupes.",
    },
    {
      q: "Quelle différence avec le minibus VIP ?",
      a: "Le van Classe V convient jusqu'à 7 personnes ; au-delà, le minibus VIP Sprinter accueille jusqu'à 16 passagers pour les groupes plus importants.",
    },
    {
      q: "Convient-il au golf et au ski ?",
      a: "Oui : son volume de chargement accueille sacs de golf, équipements de ski et valises sans difficulté.",
    },
    vehiclePriceFaq("fr", "CLASS_V", "d'un van avec chauffeur"),
    {
      q: "Comment réserver ?",
      a: "Indiquez-nous votre trajet, vos horaires et le nombre de passagers : nous confirmons rapidement le van et votre chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Minibus VIP (jusqu'à 16 places)",
      href: "/chauffeur/minibus-vip",
    },
    {
      label: "Berline avec chauffeur (Classe E)",
      href: "/chauffeur/berline-avec-chauffeur",
    },
    { label: "Tous nos services chauffeur", href: "/chauffeur" },
    {
      label: "Cortège de mariage à Cannes",
      href: "/chauffeur/voiture-mariage-cannes",
    },
  ],
  cta: {
    title: "Un transfert en famille ou en groupe ?",
    text: "Dites-nous votre trajet et votre effectif — devis rapide pour votre van avec chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis",
  },
  seo: {
    ...vehicleSeo(
      "fr",
      "CLASS_V",
      "Van avec chauffeur 7 places (Classe V)",
      "Van Mercedes Classe V 7 places avec chauffeur",
    ),
    canonical: "/chauffeur/van-avec-chauffeur",
  },
};

export default vanAvecChauffeur;
